import { Helmet } from "react-helmet-async";
import GlobalVar from "utils/config";

export const ArticleSEO = ({ article }) => {
  if (!article) return null;

  const journalTitle = "Journal of Transportation Science and Technology";
  const siteUrl = process.env.NODE_ENV === 'production' ? "https://jtst.ut.edu.vn" : GlobalVar.FRONTEND_URI;
  
  return (
    <Helmet>
      {/* Basic SEO */}
      <title>{article.info?.title} - {journalTitle}</title>
      <meta name="description" content={article.info?.summary || article.info?.title} />
      
      {/* Google Scholar Meta Tags */}
      <meta name="citation_title" content={article.info?.title} />
      <meta name="citation_journal_title" content={journalTitle} />
      
      {/* Authors */}
      {article.info?.authors?.map((author, index) => (
        <meta key={index} name="citation_author" content={`${author.lastname}, ${author.firstname}`} />
      ))}
      
      {/* Publication Details */}
      {article.publishedDate && (
        <meta 
          name="citation_publication_date" 
          content={new Date(article.publishedDate).getFullYear().toString()} 
        />
      )}
      
      {/* Volume and Issue */}
      {article.volume?.name && (
        <meta name="citation_volume" content={article.volume.name.replace('Volume ', '')} />
      )}
      {article.issue?.name && (
        <meta name="citation_issue" content={article.issue.name.replace('Issue ', '')} />
      )}
      
      {/* Page Numbers */}
      {article.pageNumberStart && (
        <meta name="citation_firstpage" content={article.pageNumberStart} />
      )}
      {article.pageNumberEnd && article.pageNumberEnd !== "0" && (
        <meta name="citation_lastpage" content={article.pageNumberEnd} />
      )}
      
      {/* DOI */}
      {article.doiLink && (
        <meta name="citation_doi" content={article.doiLink.replace('https://www.doi.org/', '').replace('https://doi.org/', '')} />
      )}
      
      {/* PDF URL - Adjust this URL based on your download endpoint */}
      <meta name="citation_pdf_url" content={`${siteUrl}/articles/${article._id}/download`} />
      
      {/* Keywords */}
      {article.info?.keywords && article.info.keywords.length > 0 && (
        <meta name="citation_keywords" content={article.info.keywords.filter(k => k && k.trim()).join(', ')} />
      )}
      
      {/* Abstract */}
      {article.info?.summary && (
        <meta name="citation_abstract" content={article.info.summary} />
      )}
      
      {/* Language */}
      <meta name="citation_language" content={article.info?.language || (article.info?.title?.match(/[àáạảãâầấậẩẫăắằặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/) ? 'vi' : 'en')} />
      
      {/* Open Graph */}
      <meta property="og:title" content={article.info?.title} />
      <meta property="og:description" content={article.info?.summary || article.info?.title} />
      <meta property="og:url" content={`${siteUrl}/articles/${article._id}`} />
      <meta property="og:type" content="article" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={`${siteUrl}/articles/${article._id}`} />
      
      {/* Structured Data for Scholar */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ScholarlyArticle",
          "headline": article.info?.title,
          "abstract": article.info?.summary,
          "author": article.info?.authors?.map(author => ({
            "@type": "Person",
            "name": `${author.firstname} ${author.lastname}`,
            "email": author.email
          })),
          "datePublished": article.publishedDate ? new Date(article.publishedDate).toISOString() : undefined,
          "publisher": {
            "@type": "Organization",
            "name": journalTitle
          },
          "isPartOf": {
            "@type": "PublicationIssue",
            "issueNumber": article.issue?.name?.replace('Issue ', ''),
            "isPartOf": {
              "@type": "PublicationVolume",
              "volumeNumber": article.volume?.name?.replace('Volume ', ''),
              "isPartOf": {
                "@type": "Periodical",
                "name": journalTitle
              }
            }
          },
          "pageStart": article.pageNumberStart,
          "pageEnd": article.pageNumberEnd !== "0" ? article.pageNumberEnd : undefined,
          "identifier": article.doiLink ? [{
            "@type": "PropertyValue",
            "propertyID": "DOI",
            "value": article.doiLink
          }] : undefined,
          "url": `${siteUrl}/articles/${article._id}`,
          "sameAs": article.doiLink
        })}
      </script>
    </Helmet>
  );
};
